<template>
    <div>
        <v-navigation-drawer v-model="categoryLegend" app clipped height="100vh">
            <search-navigation>
            </search-navigation>
        </v-navigation-drawer>

      <!--
      this HAS to be mounted at least,
      because some components inside (or example regarding years filter)
      depend on onMount to be able to work properly
      -->
        <div v-show="isPageInLoadingState">
          <v-container>
            <v-row class="mt-10">
              <v-col class="text-center">
                  <v-progress-circular size="40" indeterminate color="primary">
                  </v-progress-circular>
              </v-col>
          </v-row>
          </v-container>
        </div>
        <div v-show="!isPageInLoadingState">
          <div class="breadcrumb-container">
            <v-container>
                <v-row>
                    <v-col>
                        <breadcrumb
                            :items="breadcrumbItems">
                        </breadcrumb>
                        <div class="pt-3">
                            <h4 class="text-h4 font-weight-medium">
                                {{ displayActiveCategory }}
                            </h4>
                        </div>
                        <div v-if="breadcrumbData.description" v-html="breadcrumbData.description"></div>
                    </v-col>
                </v-row>
            </v-container>
          </div>
          <div class="grey lighten-5 container-height">
              <v-container>
                  <v-row>
                      <v-col cols="12" sm="9">
                          <default-codes-page v-if="isDefaultCodesPage"></default-codes-page>
                          <template v-else>
                              <astm-subjects v-if="isAstmSubjectPage && isAstmCompassEnabled()"></astm-subjects>
                              <template v-else>

                                  <codes-result-navigation-controls
                                      v-show="showTitles"
                                      :category="searchData.category"
                                      :show-years="showYearFilter"
                                      :loading="resultsLoading">
                                  </codes-result-navigation-controls>

                                  <template v-if="showTitles">

                                      <p class="mb-2 mt-2 font-weight-bold">{{ pagination.totalResults }} titles</p>

                                      <title-list v-if="pagination.totalResults > 0" :titles="results"
                                          :loading="resultsLoading" :itemsPerPage="itemsPerPage" displayBorder
                                          displayUserTypeLabel :displayInCols="isMobilePort ? 12 : 6">
                                      </title-list>
                                      <v-row justify="center" v-else>
                                          <v-col cols="auto">
                                              <no-results-found></no-results-found>
                                          </v-col>
                                      </v-row>
                                  </template>

                                  <v-row v-if="page < pagination.maxPerPage && !resultsLoading && showTitles" dense justify="center">
                                      <v-col cols="auto">
                                          <v-btn text color="primary" class="mb-4" :loading="showMoreLoading" @click="showMore">
                                              Show More
                                              <v-icon right>
                                                  mdi-chevron-down
                                              </v-icon>
                                          </v-btn>
                                      </v-col>
                                  </v-row>

                                <template v-if="showAdditionalOptions && hasAdditionalOptions">

                                  <!-- divider ONLY necessary if titles are shown above -->
                                  <v-divider v-if="showTitles" class="my-5"></v-divider>

                                  <h2 v-if="showTitles" class="text-h6 font-weight-medium mb-4">
                                    Additional Options in {{ activeCategory }}
                                  </h2>

                                  <v-row>
                                    <v-col v-for="(category, index) in activeCategoryChildCategories"
                                          :key="category.id"
                                          cols="12"
                                          sm="6">
                                          <component :is="category.fullUrl ? 'a' : 'div'" :href="category.fullUrl || null">
                                              <v-card class="pa-4 hover-border rounded-lg" outlined>
                                                  <div class="text-body-1 font-weight-medium">{{ category.name }}</div>
                                              </v-card>
                                          </component>
                                    </v-col>
                                  </v-row>

                                </template>

                              </template>
                          </template>
                      </v-col>
                      <v-col cols="12" sm="3">
                          <category-tabs
                              :loading="resultsLoading || isCategoryDataLoading"
                              :category-data="categoryData">
                          </category-tabs>
                      </v-col>
                  </v-row>
              </v-container>
          </div>
        </div>
    </div>
</template>

<script>
    import Codes from './mixins/codes';
    import CategoryTabs from './partials/categoryTabs';
    import AstmSubjects from './astm/Subjects';
    import CodesResultNavigationControls from "./CodesResultNavigationControls";
    import NoResultsFound from "./NoResultsFound";
    import TitleList from '../content/title/TitleList.vue';
    import DefaultCodesPage from './DefaultCodesPage.vue';
    import Mobile from '../inc/mixins/mobile';
    import {ApiService} from "../../api/ApiService";
    import { StringUtil } from '../../util/StringUtil';

    export default {
        data() {
            return {
                breadcrumbItems: [],
              // because resultsLoading is true by default too
                isCategoryDataLoading: true,
                categoryData: null
            }
        },
        mixins: [Mobile, Codes],
        components: {
            NoResultsFound,
            CodesResultNavigationControls,
            CategoryTabs,
            AstmSubjects,
            TitleList,
            DefaultCodesPage
        },
      computed: {
        isPageInLoadingState() {
          if (this.isDefaultCodesPage) {
            return this.resultsLoading;
          }

          return this.resultsLoading || this.isCategoryDataLoading;
        },
        displayActiveCategory() {
           return this.activeCategory ?? 'Codes';
        },
        activeCategoryChildCategories() {
          const { activeCategory, categories = [] } = this;
          if (!activeCategory) return [];

          const parentSlug = categories.find(c => c.name === activeCategory)?.slug;
          if (!parentSlug) return [];

          const children = categories.filter(c => c.parentCategory === parentSlug);

          return StringUtil.sortTitleCategories(children);
        },
        showTitles() {
          return this.categoryData && this.categoryData.show_titles;
        },
        showAdditionalOptions() {
          return this.categoryData && this.categoryData.show_categories;
        },
        hasAdditionalOptions() {
          return this.activeCategoryChildCategories.length > 0;
        }
      },
      methods: {
        buildBreadcrumbsFromCategory(category) {

          const flat = (this.categories || []);

          // find that active category first!
          const result = flat.find((item) => item.name === category);
          const bcItems = [];

          if (result !== undefined) {

            // actual category that we are in - link DISABLED by default
            bcItems.push({
              text: result.name,
              disabled: true,
              href: result.slug
            });

            let currentCategory = result;

            while (currentCategory.parentCategory) {

              // find category object itself
              const temp = flat.find((item) => item.slug === currentCategory.parentCategory);

              if (temp) {
                bcItems.unshift({
                  text: temp.name,
                  href: temp.slug
                });

                currentCategory = temp;
              }
            }

            // we are in some category at least - add /codes category as first link by default
            bcItems.unshift({
              text: 'Codes',
              href: '/codes'
            });
          }

          for (let i = 1; i < bcItems.length; i++) {
            bcItems[i].href = bcItems[i - 1].href + '/' + bcItems[i].href;
          }

          return bcItems;
        },
        getCategoryInfo(categoryName) {

          this.isCategoryDataLoading = true;

          ApiService.getCategoryInfo(categoryName).then((data) => {
            this.categoryData = data;
          }).finally(() => {
            this.isCategoryDataLoading = false;
          });
        }
      },
      watch: {
        activeCategory: {
          immediate: true,
          handler(newVal) {
            this.breadcrumbItems = this.buildBreadcrumbsFromCategory(newVal);
            if (newVal) this.getCategoryInfo(newVal);
          }
        }
      },
    }
</script>

<style lang="scss" scoped>
    .sr-only {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
    }
    .breadcrumb-container {
      border-bottom: 1px solid #0000001F !important;
      min-height: 84px;
    }
</style>